/**
 * TypeScript error response contracts for the C5P API (Django REST Framework)
 *
 * This file describes the shape of all HTTP error responses returned by the backend.
 * Error bodies are JSON. All responses include the following response headers:
 *   - X-Tenant-Id:   UUID of the authenticated user's tenant (on /api/* routes)
 *   - X-App-Version: Backend application version string
 *   - X-App-Name:    Application name
 *
 * Status codes covered: 400, 401, 402, 403, 404, 422, 429, 500
 *
 * Section index:
 *   1. Base shapes (DRF primitives)
 *   2. 400 Bad Request
 *   3. 401 Unauthorized
 *   4. 402 Payment Required
 *   5. 403 Forbidden
 *   6. 404 Not Found
 *   7. 422 Unprocessable Entity
 *   8. 429 Too Many Requests
 *   9. 500 Internal Server Error
 *  10. KLIC Delivery endpoint errors (consolidated)
 *  11. Error type guards
 */

// ============================================================================
// 1. Base shapes (DRF primitives)
// ============================================================================

/**
 * Standard DRF single-message error.
 * Used by auth/permission/throttle exceptions.
 *
 * @example { "detail": "Authentication credentials were not provided." }
 */
export interface DRFDetailError {
  detail: string
}

/**
 * DRF field-level validation error.
 * Keys are serializer field names; values are arrays of error strings.
 *
 * @example { "klic_number": ["This field may not be blank."], "initiative": ["This field is required."] }
 */
export type DRFFieldErrors = Record<string, string[]>

/**
 * DRF non-field validation error (schema-level, not tied to a single field).
 *
 * @example { "non_field_errors": ["The fields initiative, klic_number must make a unique set."] }
 */
export interface DRFNonFieldErrors {
  non_field_errors: string[]
}

/**
 * Custom single-message error used by views that build responses directly
 * (e.g. KlicDeliveryViewSet.reprocess, InvitationViewSet.resend).
 *
 * @example { "error": "Delivery is already being processed" }
 */
export interface CustomError {
  error: string
}

/**
 * Machine-readable error codes used by the invitation endpoints.
 *
 * - RATE_LIMIT_EXCEEDED  — Too many invitations sent within the rate-limit window
 * - INVALID_STATUS       — Invitation is not in the expected status (e.g. not pending)
 * - VALIDATION_ERROR     — General validation failure in the invitation service layer
 * - INTERNAL_ERROR       — Unexpected server-side error during invitation processing
 */
export type InvitationErrorCode =
  | 'RATE_LIMIT_EXCEEDED'
  | 'INVALID_STATUS'
  | 'VALIDATION_ERROR'
  | 'INTERNAL_ERROR'

/**
 * Custom error with a machine-readable code.
 * Used when the caller needs to distinguish specific error conditions.
 *
 * @example { "error": "Rate limit exceeded. Try again later.", "error_code": "RATE_LIMIT_EXCEEDED" }
 */
export interface CustomCodedError {
  error: string
  error_code: string
}

/**
 * Strongly-typed coded error for invitation endpoints.
 *
 * @example { "error": "Invitation is not pending or expired", "error_code": "INVALID_STATUS" }
 */
export interface InvitationCodedError {
  error: string
  error_code: InvitationErrorCode
}

/**
 * Errors array format used by import/export and bulk-operation endpoints.
 * Unlike DRFFieldErrors (record keyed by field name), this is a flat list
 * of human-readable strings describing what went wrong.
 *
 * @example { "errors": ["File encoding error. Please ensure the file is UTF-8 encoded."] }
 * @example { "errors": ["JSON file must contain an array of objects"] }
 */
export interface ErrorsArrayError {
  errors: string[]
}

// ============================================================================
// 2. 400 Bad Request
// ============================================================================

/**
 * 400 — Field-level validation failure from a DRF serializer.
 *
 * Returned when serializer.is_valid(raise_exception=True) fails.
 * Each key is a field name; the value is a non-empty list of human-readable
 * error strings. May also include "non_field_errors" for cross-field rules.
 *
 * Common triggers (KLIC Delivery):
 *   - initiative: "Initiatief bestaat niet." — initiative UUID not found
 *   - klic_number, klic_type, source_file: standard DRF field validation
 *
 * @example
 * // Single field error
 * { "initiative": ["Initiatief bestaat niet."] }
 *
 * // Multiple fields
 * { "klic_number": ["This field may not be blank."], "klic_type": ["\"invalid\" is not a valid choice."] }
 */
export type ValidationError400 = DRFFieldErrors | DRFNonFieldErrors

/**
 * 400 — Reprocess action: delivery cannot be reprocessed.
 *
 * Returned by POST /api/klic_deliveries/{id}/reprocess/ when:
 *   - The delivery has no source file (shadow admin record)
 *   - The delivery is already in PROCESSING status
 *   - The delivery's reprocess() method raises a ValueError
 *
 * @example { "error": "Cannot reprocess shadow admin record (no source file)" }
 * @example { "error": "Delivery is already being processed" }
 */
export type ReprocessError400 = CustomError

/**
 * 400 — State machine transition not allowed.
 *
 * Returned by POST /api/initiatives/{id}/transition/ when the requested
 * event is not a valid transition from the initiative's current state.
 * The body is intentionally empty — check only the 400 status code.
 *
 * @example  (empty body — no JSON)
 */
export type TransitionError400 = null

/**
 * 400 — Import file error (bad encoding, invalid structure, or service ValueError).
 *
 * Returned by import-csv and import-json endpoints when the uploaded file
 * cannot be parsed at all (before the import pipeline runs).
 *
 * @example { "errors": ["File encoding error. Please ensure the file is UTF-8 encoded."] }
 * @example { "errors": ["JSON file must contain an array of objects"] }
 */
export type ImportFileError400 = ErrorsArrayError

/**
 * 400 — Invitation action error (coded).
 *
 * Returned by POST /api/invitations/{id}/resend/ and related invitation
 * endpoints when the service layer raises a recoverable ValueError.
 *
 * error_code values:
 *   - "INVALID_STATUS"   — invitation is not in a resendable state
 *   - "VALIDATION_ERROR" — other validation issue in the service layer
 *
 * @example { "error": "Invitation is not pending or expired", "error_code": "INVALID_STATUS" }
 */
export type InvitationError400 = InvitationCodedError

/**
 * Union of all 400 response bodies across the API.
 */
export type AnyError400 =
  | ValidationError400
  | ReprocessError400
  | TransitionError400
  | ImportFileError400
  | InvitationError400
  | CustomError
  | ErrorsArrayError

// ============================================================================
// 3. 401 Unauthorized
// ============================================================================

/**
 * 401 — Request lacks valid authentication credentials.
 *
 * Returned by DRF's default IsAuthenticated permission check on all
 * authenticated endpoints when:
 *   - No session cookie / token is present
 *   - The session has expired
 *   - The CSRF token is missing or invalid (on session-authenticated requests)
 *
 * @example { "detail": "Authentication credentials were not provided." }
 * @example { "detail": "Invalid token." }
 */
export type AuthenticationError401 = DRFDetailError

// ============================================================================
// 4. 402 Payment Required
// ============================================================================

/**
 * 402 — Tenant subscription does not include the requested feature.
 *
 * Returned by POST /api/klic_deliveries/{id}/reprocess/ when the
 * initiative's owner tenant has klic_meldingen_enabled = False.
 *
 * The reprocess endpoint returns this as a plain Response (not via the
 * PaymentRequired exception class), so the body shape is CustomError.
 *
 * @example { "error": "KLIC meldingen feature is not enabled for your subscription." }
 */
export type SubscriptionError402 = CustomError

// ============================================================================
// 5. 403 Forbidden
// ============================================================================

/**
 * 403 — Authenticated user lacks permission for the requested action.
 *
 * Two shapes are possible depending on where the check fires:
 *
 * Shape A — DRF PermissionDenied (most viewset action-level checks):
 *   { "detail": "You do not have permission to perform this action." }
 *
 * Shape B — Custom PermissionDenied raised in KlicDeliveryViewSet.perform_create:
 *   { "detail": "You do not have access to this initiative." }
 *
 * Common triggers (KLIC Delivery):
 *   - User is not a poweruser for the initiative's tenant AND is not a member
 *     of the initiative → "You do not have access to this initiative."
 *   - User's role does not satisfy VIEWSET_ACTION_PERMISSIONS for the action
 *
 * @example { "detail": "You do not have access to this initiative." }
 * @example { "detail": "You do not have permission to perform this action." }
 */
export type PermissionError403 = DRFDetailError

// ============================================================================
// 6. 404 Not Found
// ============================================================================

/**
 * 404 — Requested resource does not exist (or is outside the user's scope).
 *
 * DRF returns this shape from get_object() when the queryset lookup fails.
 * The backend intentionally uses 404 (instead of 403) when a resource exists
 * but belongs to another tenant — preventing information leakage.
 *
 * @example { "detail": "Not found." }
 */
export type NotFoundError404 = DRFDetailError

// ============================================================================
// 7. 422 Unprocessable Entity
// ============================================================================

/**
 * 422 — Import pipeline validation failure.
 *
 * Returned by POST /api/standard_risk_mitigations/import-csv/ and
 * POST /api/standard_risk_mitigations/import-json/ when the file parses
 * successfully but the import pipeline detects semantic errors.
 *
 * The same shape is returned as HTTP 200 on success (success: true) and
 * HTTP 422 on failure (success: false). Always inspect the `errors` array
 * when success is false.
 *
 * Fields:
 *   success        — false on 422, true on 200
 *   warnings       — non-fatal issues; import may still proceed
 *   errors         — fatal issues that prevented the import
 *   import_preview — preview of what would be imported (null on failure)
 *   total_records  — number of records found in the file (0 on failure)
 *
 * @example
 * {
 *   "success": false,
 *   "warnings": ["Row 3: description exceeds recommended length"],
 *   "errors": ["Row 5: required field 'title' is missing"],
 *   "import_preview": null,
 *   "total_records": 0
 * }
 */
export interface ImportValidationError422 {
  success: false
  warnings: string[]
  errors: string[]
  import_preview: null
  total_records: number
}

/**
 * Successful import result (HTTP 200) — same shape as 422 but with success: true.
 * Included here for completeness so callers can use a single type for the response.
 */
export interface ImportSuccess200 {
  success: true
  warnings: string[]
  errors: string[]
  import_preview: Record<string, unknown> | null
  total_records: number
}

// ============================================================================
// 8. 429 Too Many Requests
// ============================================================================

/**
 * 429 — Request has been rate-limited.
 *
 * Two mechanisms exist, producing different shapes:
 *
 * Shape A — DRF UserRateThrottle (ContactViewSet.global_lookup):
 *   Rate: 30 req/min per authenticated user.
 *   Body matches DRFDetailError; the detail message includes a retry hint.
 *
 * Shape B — Cache-based rate limiter (InvitationViewSet.resend):
 *   Rate: 10 invitations/hour per user (INVITATION_RATE_LIMIT_PER_HOUR setting).
 *   Superusers bypass this limit. Body matches CustomCodedError.
 *
 * @example Shape A: { "detail": "Request was throttled. Expected available in 42 seconds." }
 * @example Shape B: { "error": "Rate limit exceeded. You can send at most 10 invitations per hour.", "error_code": "RATE_LIMIT_EXCEEDED" }
 */
export type RateLimitError429 = DRFDetailError | CustomCodedError

/**
 * 429 shape A — DRF throttle response (includes retry timing).
 *
 * The detail string follows DRF's default template:
 * "Request was throttled. Expected available in N seconds."
 */
export interface DRFThrottleError429 {
  detail: string
}

/**
 * 429 shape B — Invitation rate limit (cache-based, custom error code).
 */
export interface InvitationRateLimitError429 {
  error: string
  error_code: 'RATE_LIMIT_EXCEEDED'
}

// ============================================================================
// 9. 500 Internal Server Error
// ============================================================================

/**
 * 500 — Unexpected server-side error.
 *
 * Three shapes are produced depending on where the error is caught:
 *
 * Shape A — CustomError (KLIC reprocess, invitation revoke):
 *   { "error": "Reprocessing failed: <exc>" }
 *   { "error": "Failed to revoke invitation" }
 *
 * Shape B — InvitationCodedError (invitation resend unexpected exception):
 *   { "error": "An unexpected error occurred while resending the invitation", "error_code": "INTERNAL_ERROR" }
 *
 * Shape C — ErrorsArrayError (import-csv, import-json, export-csv, export-json):
 *   { "errors": ["Unexpected error: <exc>"] }
 *   { "errors": ["Export failed: <exc>"] }
 *
 * Shape D — HTML (Django's default 500 handler in non-DEBUG mode, not JSON).
 *   Only happens if the request never reached DRF's exception handler.
 *
 * @example Shape A: { "error": "Reprocessing failed: connection timeout" }
 * @example Shape B: { "error": "An unexpected error occurred while resending the invitation", "error_code": "INTERNAL_ERROR" }
 * @example Shape C: { "errors": ["Export failed: permission denied"] }
 */
export type InternalServerError500 =
  | CustomError
  | InvitationCodedError
  | ErrorsArrayError

// ============================================================================
// 10. KLIC Delivery endpoint errors (consolidated)
// ============================================================================

/**
 * All possible error responses for the KLIC Delivery endpoints.
 *
 * Endpoint map:
 *
 * POST   /api/klic_deliveries/
 *   400  ValidationError400          — serializer validation failed
 *   400  { initiative: string[] }    — initiative UUID not found
 *   401  AuthenticationError401      — not authenticated
 *   403  PermissionError403          — not poweruser and not initiative member
 *
 * GET    /api/klic_deliveries/
 *   401  AuthenticationError401      — not authenticated
 *
 * GET    /api/klic_deliveries/{id}/
 *   401  AuthenticationError401      — not authenticated
 *   404  NotFoundError404            — delivery not found / outside tenant scope
 *
 * PATCH / PUT  /api/klic_deliveries/{id}/
 *   400  ValidationError400          — serializer validation failed
 *   401  AuthenticationError401      — not authenticated
 *   403  PermissionError403          — insufficient role
 *   404  NotFoundError404            — delivery not found
 *
 * DELETE /api/klic_deliveries/{id}/
 *   401  AuthenticationError401      — not authenticated
 *   403  PermissionError403          — insufficient role
 *   404  NotFoundError404            — delivery not found
 *
 * POST   /api/klic_deliveries/{id}/reprocess/
 *   400  ReprocessError400           — no source file, already processing, or ValueError
 *   401  AuthenticationError401      — not authenticated
 *   402  SubscriptionError402        — klic_meldingen_enabled = False for tenant
 *   403  PermissionError403          — insufficient role
 *   404  NotFoundError404            — delivery not found
 *   500  InternalServerError500      — unexpected exception during reprocessing
 */

export interface KlicDeliveryErrorMap {
  400: ValidationError400 | ReprocessError400
  401: AuthenticationError401
  402: SubscriptionError402
  403: PermissionError403
  404: NotFoundError404
  500: InternalServerError500
}

/**
 * Reprocess-specific error union — all possible error bodies for the
 * POST /api/klic_deliveries/{id}/reprocess/ action.
 */
export type KlicDeliveryReprocessError =
  | ReprocessError400
  | AuthenticationError401
  | SubscriptionError402
  | PermissionError403
  | NotFoundError404
  | InternalServerError500

/**
 * Successful reprocess response (HTTP 200).
 * Returned when the reprocess job is successfully enqueued.
 *
 * @example { "status": "reprocessing", "message": "Delivery reprocessing started", "klic_number": "22G012345" }
 */
export interface KlicDeliveryReprocessSuccess {
  status: 'reprocessing'
  message: string
  klic_number: string
}

// ============================================================================
// 11. Type guards
// ============================================================================

/** Returns true when the body is a DRF detail error (single "detail" string). */
export function isDRFDetailError(body: unknown): body is DRFDetailError {
  return (
    typeof body === 'object' &&
    body !== null &&
    'detail' in body &&
    typeof (body as DRFDetailError).detail === 'string'
  )
}

/** Returns true when the body is a plain custom error (single "error" string). */
export function isCustomError(body: unknown): body is CustomError {
  return (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof (body as CustomError).error === 'string' &&
    !('error_code' in body)
  )
}

/** Returns true when the body is a coded custom error. */
export function isCustomCodedError(body: unknown): body is CustomCodedError {
  return (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    'error_code' in body &&
    typeof (body as CustomCodedError).error_code === 'string'
  )
}

/** Returns true when the body contains DRF field-level validation errors. */
export function isDRFFieldErrors(body: unknown): body is DRFFieldErrors {
  return (
    typeof body === 'object' &&
    body !== null &&
    !('detail' in body) &&
    !('error' in body) &&
    !('errors' in body) &&
    Object.keys(body as object).length > 0 &&
    Object.values(body as object).every(
      (v) => Array.isArray(v) && v.every((s) => typeof s === 'string'),
    )
  )
}

/**
 * Returns true when the body is the errors-array format (import/export/bulk endpoints).
 * Requires that all elements of the array are strings (guards against allauth-style
 * error arrays that contain objects with { param, message } shapes).
 */
export function isErrorsArrayError(body: unknown): body is ErrorsArrayError {
  return (
    typeof body === 'object' &&
    body !== null &&
    'errors' in body &&
    Array.isArray((body as ErrorsArrayError).errors) &&
    (body as ErrorsArrayError).errors.every((e) => typeof e === 'string')
  )
}

/** Returns true when the body is a 422 import validation failure. */
export function isImportValidationError422(
  body: unknown,
): body is ImportValidationError422 {
  return (
    typeof body === 'object' &&
    body !== null &&
    'success' in body &&
    (body as ImportValidationError422).success === false &&
    'errors' in body &&
    Array.isArray((body as ImportValidationError422).errors)
  )
}

/**
 * Narrow any API error response body to a human-readable string.
 * Handles all known DRF error shapes. Returns undefined when the body
 * does not match any known shape (caller should fall back to a generic message).
 *
 * Usage:
 * ```typescript
 * const text = describeApiError(response._data)
 * ```
 */
export function describeApiError(body: unknown): string | undefined {
  if (body === null || body === undefined) return undefined
  if (typeof body !== 'object') return undefined // HTML or plain-text (e.g. Django 500 in non-DEBUG mode)
  if (isDRFDetailError(body)) return body.detail
  if (isCustomCodedError(body)) return body.error
  if (isCustomError(body)) return body.error
  if (isErrorsArrayError(body)) return body.errors.join(' ')
  if (isImportValidationError422(body)) return body.errors.join(' ')
  if (isDRFFieldErrors(body)) {
    return Object.entries(body as DRFFieldErrors)
      .map(([field, msgs]) => `${field}: ${msgs.join(', ')}`)
      .join('; ')
  }
  return undefined
}

/**
 * Narrow a KLIC delivery reprocess error response to a human-readable string.
 *
 * Usage:
 * ```typescript
 * const text = describeKlicReprocessError(body)
 * ```
 */
export function describeKlicReprocessError(
  body: KlicDeliveryReprocessError,
): string | undefined {
  return describeApiError(body)
}
